import { Component, Input, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe, AsyncPipe } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { Observable } from 'rxjs';

export interface MaterialTotalQuantityCellRendererParams {
  UMPActive$: Observable<boolean>;
}

/**
 * Material Total Quantity Cell Renderer
 *
 * Displays total quantity information for GroupedMaterialWithCustomers with:
 * - Total base quantity (UMB) with unit (reads from totalBaseQuantity)
 * - Total parallel quantity (UMP) with unit (if UMPActive$ observable emits true) (reads from totalPararellQuantity)
 *
 * Uses async pipe for reactive UMPActive updates.
 *
 * @example
 * ```typescript
 * {
 *   colId: 'total',
 *   headerName: 'Total',
 *   cellRenderer: MaterialTotalQuantityCellRendererComponent,
 *   cellRendererParams: {
 *     UMPActive$: this.UMPActive$
 *   } as MaterialTotalQuantityCellRendererParams,
 *   field: 'totalBaseQuantity',
 *   minWidth: 200
 * }
 * ```
 */
@Component({
  selector: 'app-material-total-quantity-cell-renderer',
  standalone: true,
  imports: [CommonModule, AsyncPipe, DecimalPipe],
  template: `
    <div class="table-text-m-2 text-sm">
      <div class="mb-1">
        <b>UMB:</b> {{ params?.data?.totalBaseQuantity | number: '1.3' }}
        <span class="text-gray-500">
          ({{ params?.data?.baseUnitOfMeasure?.name ?? '-' }})
        </span>
      </div>

      <div *ngIf="(customParams?.UMPActive$ | async)">
        <b>UMP:</b>
        <ng-container *ngIf="params?.data?.pararellUnitOfMeasure?.name; else noUMP">
          {{ params?.data?.totalPararellQuantity | number: '1.3' }}
          <span class="text-gray-500">
            ({{ params?.data?.pararellUnitOfMeasure?.name }})
          </span>
        </ng-container>
        <ng-template #noUMP>
          <span class="text-gray-500">-</span>
        </ng-template>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `]
})
export class MaterialTotalQuantityCellRendererComponent implements CellRenderer<any>, OnInit {
  @Input() params!: CellRendererParams<any>;

  get customParams(): MaterialTotalQuantityCellRendererParams | undefined {
    return this.params?.colDef?.cellRendererParams as MaterialTotalQuantityCellRendererParams;
  }

  ngOnInit() {
    // Component initialized
  }
}
